import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Copy, Eye, Truck } from 'lucide-react';
import axios from 'axios';
import RecordHistory from '../nativeitems/RecordHistory';
import RelatedRecords from '../nativeitems/RelatedRecords';
import AllFieldsPanel from './AllFieldsPanel';
import AttachmentsTab from './AttachmentsTab';
import DocumentActions from './DocumentActions';
import { useRecordDetail, fmtQty, fmtMoney, fmtDate, statusLabel, SO_STATUS } from './recordUtils';
import '../nativeitems/NativeItems.css';

// Sales order record (Native, read-only) — the "open the SO in NetSuite"
// replacement (tasks #62/#60). Served from the synced sales_orders /
// sales_order_lines mirror; the All-fields tab renders the full ns_raw
// capture; Related + History run on the native twin when one exists.

// Assembly/kit COMPONENT lines ride on NS sales orders as negative-quantity,
// zero-amount rows (the vessel/wax/oil/labels consumed when the assembly was
// built). They're kept in the mirror but hidden from the default view — a
// toggle reveals them. Real zero-dollar lines (free goods) have positive
// quantity and stay visible.
const isComponentLine = (l) =>
  Number(l.quantity_ordered ?? l.quantity ?? 0) < 0 && !(Number(l.amount) || 0);

// NetSuite-parity Fulfill: a still-shippable order gets a Fulfill button that
// opens the native fulfillment form prefilled with its open lines. Same
// status set Count's record page and SO modal use for mirror-backed orders.
const SHIPPABLE_SO = ['B', 'D', 'E', 'pending_fulfillment', 'partially_fulfilled', 'pending_billing_partially_fulfilled'];
// Native-only orders (no NS twin — lockout captures, store-PO generations)
// use their own status vocabulary (packages/orders ORDER_STATUSES) and only
// the 'manual' channel resolves through /api/native-orders/:id (the fulfill
// form's order picker filters to that channel too).
const SHIPPABLE_NATIVE_SO = ['open', 'partially_fulfilled'];

export default function SalesOrderRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [showComponents, setShowComponents] = useState(false);
  const { data, loading, error } = useRecordDetail(`/api/native-records/sales-orders/${encodeURIComponent(id)}`);

  if (loading) return <div className="nit-page"><div className="nit-loading">Loading sales order…</div></div>;
  if (error) {
    return (
      <div className="nit-page">
        <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
        <div className="nit-error">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { record, lines, nativeOrderId, sourcePurchaseOrder } = data;
  const canFulfill = record.native !== true && record.order_number && SHIPPABLE_SO.includes(record.status);
  const canFulfillNative = record.native === true && record.channel === 'manual' && nativeOrderId
    && SHIPPABLE_NATIVE_SO.includes(record.status);
  const fulfillHref = canFulfill
    ? `/native-fulfillments/new?so=${encodeURIComponent(record.order_number)}`
    : canFulfillNative
      ? `/native-fulfillments/new?nativeOrderId=${encodeURIComponent(nativeOrderId)}`
      : null;
  const componentCount = lines.filter(isComponentLine).length;
  const visibleLines = showComponents ? lines : lines.filter((l) => !isComponentLine(l));
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'lines', label: 'Lines', count: lines.length - componentCount },
    { key: 'fields', label: 'All fields' },
    { key: 'related', label: 'Related' },
    { key: 'history', label: 'History' },
    { key: 'attachments', label: 'Attachments' },
  ];

  return (
    <div className="nit-page">
      <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>

      <div className="nit-item-head" style={{ marginTop: 8 }}>
        <code>SO {record.order_number || record.ns_internal_id}</code>
        <h1>{record.customer_name || 'Sales order'}</h1>
        <span className="nit-tag">{statusLabel(SO_STATUS, record.status)}</span>
        {record.voided && <span className="nit-tag nit-tag-inactive">voided</span>}
        {record.ns_internal_id && <span className="nit-tag">NS {record.ns_internal_id}</span>}
        {record.native === true && <span className="nit-tag">native</span>}
        <span className="nit-readonly"><Eye size={12} /> read-only</span>
        {/* The transaction this order was generated from (store reorder POs). */}
        {sourcePurchaseOrder && (
          <Link to={`/records/purchase-orders/${encodeURIComponent(sourcePurchaseOrder.id)}`} className="nit-btn" style={{ marginLeft: 'auto', textDecoration: 'none' }}>
            From PO {sourcePurchaseOrder.number} →
          </Link>
        )}
        {fulfillHref && (
          <Link
            to={fulfillHref}
            className="nit-btn nit-btn-primary"
            style={{ marginLeft: sourcePurchaseOrder ? 0 : 'auto', textDecoration: 'none' }}
          >
            <Truck size={14} /> Fulfill
          </Link>
        )}
        <Link to={`/native-orders/new?copyFrom=${encodeURIComponent(id)}`} className="nit-btn" style={{ marginLeft: (sourcePurchaseOrder || fulfillHref) ? 0 : 'auto', textDecoration: 'none' }}>
          <Copy size={14} /> Make Copy
        </Link>
        <button
          className="nit-btn"
          onClick={async () => {
            if (!window.confirm('Void this document? Every open line is closed on the NetSuite record (history is kept).')) return;
            try {
              const { data } = await axios.post(`/api/native-records/sales-orders/${encodeURIComponent(id)}/void`);
              window.alert(data.note || 'Voided.');
              window.location.reload();
            } catch (err) {
              window.alert(err.response?.data?.error || err.message);
            }
          }}
        >
          Void
        </button>
        <DocumentActions
          recordId={id}
          printDocs={[
            { type: 'order-confirmation', label: 'Print / Download PDF' },
            { type: 'packing-slip', label: 'Packing slip' },
          ]}
          emailDoc={{ type: 'order-confirmation', label: 'Email…' }}
        />
      </div>

      <div className="nit-tabs">
        {tabs.map((t) => (
          <button key={t.key} className={`nit-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
            {t.count != null && <span className="nit-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="nit-grid">
          <div className="nit-card">
            <h3>Order</h3>
            <div className="nit-facts">
              <span>Order date</span><strong>{fmtDate(record.order_date)}</strong>
              <span>Status</span><strong>{statusLabel(SO_STATUS, record.status)}</strong>
              <span>Total</span><strong>{fmtMoney(record.total_amount)}</strong>
              <span>Terms</span><strong>{record.terms || '—'}</strong>
              <span>Ship date</span><strong>{fmtDate(record.ship_date)}</strong>
              <span>Close date</span><strong>{fmtDate(record.close_date)}</strong>
              <span>Posting period</span><strong>{record.posting_period || '—'}</strong>
              <span>Memo</span><strong>{record.memo || '—'}</strong>
            </div>
          </div>

          <div className="nit-card">
            <h3>Shipping</h3>
            <div className="nit-facts">
              <span>Ship-to</span><strong style={{ whiteSpace: 'pre-wrap' }}>{record.ship_address || '—'}</strong>
              <span>Ship method</span><strong>{record.ship_method || '—'}</strong>
              <span>Carrier</span><strong>{record.ship_carrier || '—'}</strong>
              <span>Tracking</span><strong style={{ wordBreak: 'break-all' }}>{record.tracking_numbers || '—'}</strong>
            </div>
            {record.ship_address == null && record.ns_raw == null && (
              <p className="nit-caption">Ship-to/method/tracking arrive with the full-field capture backfill (migration 20260705).</p>
            )}
          </div>

          <div className="nit-card">
            <h3>Attribution</h3>
            <div className="nit-facts">
              <span>Customer</span><strong>{record.customer_name || '—'}</strong>
              <span>Sales rep</span><strong>{record.sales_rep_name || '—'}</strong>
              <span>Rep group</span><strong>{record.rep_group || '—'}</strong>
              <span>Order type</span><strong>{record.order_type || '—'}</strong>
              <span>Est. gross profit</span><strong>{fmtMoney(record.est_gross_profit)}</strong>
            </div>
          </div>
        </div>
      )}

      {tab === 'lines' && (
        <div className="nit-card">
          <h3>Lines</h3>
          {lines.length === 0 && <div className="nit-empty">No item lines in the mirror for this order.</div>}
          {lines.length > 0 && (
            <>
              {componentCount > 0 && (
                <label className="nit-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginBottom: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showComponents}
                    onChange={(e) => setShowComponents(e.target.checked)}
                  />
                  Show {componentCount} assembly component line{componentCount === 1 ? '' : 's'} (materials consumed at build)
                </label>
              )}
              <div className="nit-table-wrap">
                <table className="nit-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Item</th>
                      <th className="r">Ordered</th><th className="r">Shipped</th>
                      <th className="r">Picked</th><th className="r">Packed</th><th className="r">Billed</th>
                      <th>Location</th><th>Ship date</th><th>Price level</th>
                      <th className="r">Rate</th><th className="r">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLines.map((l) => (
                      <tr
                        key={l.id || l.line_number}
                        className={l.is_closed ? 'nit-inactive-row' : isComponentLine(l) ? 'nit-inactive-row' : ''}
                      >
                        <td className="nit-muted">{l.line_number}</td>
                        <td>{l.item_name || l.item_id}</td>
                        <td className="r">{fmtQty(l.quantity_ordered ?? l.quantity)}</td>
                        <td className="r">{fmtQty(l.quantity_shipped)}</td>
                        <td className="r nit-muted">{fmtQty(l.quantity_picked)}</td>
                        <td className="r nit-muted">{fmtQty(l.quantity_packed)}</td>
                        <td className="r nit-muted">{fmtQty(l.quantity_billed)}</td>
                        <td className="nit-muted">{l.location_name || '—'}</td>
                        <td>{fmtDate(l.ship_date)}</td>
                        <td className="nit-muted">{l.price_level || '—'}</td>
                        <td className="r">{fmtMoney(l.unit_price, 4)}</td>
                        <td className="r">{fmtMoney(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="nit-caption">
                Ordered vs shipped are the unconflated quantities (the legacy mirror column silently
                became the shipped amount once a line partially shipped). Picked/packed/location/price
                level fill in after the capture backfill.
              </p>
            </>
          )}
        </div>
      )}

      {tab === 'fields' && (
        <AllFieldsPanel
          family="transaction"
          nsRaw={record.ns_raw}
          overrides={{
            shipmethod: record.ship_method,
            employee: record.sales_rep_name,
            custbodysales_order_type: record.order_type,
            custbody41: record.rep_group,
          }}
        />
      )}

      {tab === 'related' && (
        nativeOrderId
          ? <RelatedRecords recordType="sales_order" id={nativeOrderId} />
          : (
            <div className="nit-card">
              <h3>Related records</h3>
              <div className="nit-empty">
                No native twin for this order yet — the Related graph runs on
                <code> native_sales_orders</code>, which shadow-writes new channel orders. Historical
                NetSuite-only orders link up as the cutover backfill lands.
              </div>
            </div>
          )
      )}

      {tab === 'history' && (
        nativeOrderId
          ? <RecordHistory table="native_sales_orders" rowId={nativeOrderId} />
          : (
            <div className="nit-card">
              <h3>Field changes</h3>
              <div className="nit-empty">
                Change capture runs on the native order tables; this mirror row has no native twin
                yet, so its field history remains in NetSuite (read-only).
              </div>
            </div>
          )
      )}

      {tab === 'attachments' && <AttachmentsTab recordType="sales_order" recordId={id} />}

      {tab === 'overview' && data.nativeOrder && (
        <p className="nit-caption">
          Native twin: {data.nativeOrder.channel} order {data.nativeOrder.external_order_number || nativeOrderId} ({data.nativeOrder.status}).
        </p>
      )}
      {tab === 'overview' && lines.length > 0 && (
        <p className="nit-caption">
          {lines.length - componentCount} lines — see the Lines tab. Items link from the <Link to="/native-items">native item browser</Link>.
        </p>
      )}
    </div>
  );
}
